<script setup lang="ts">
defineProps({ blok: Object })

const transparencyChart = ref()
</script>

<template>
    <section class="py-32">
        <UiContainer>
            <div class="py-12 px-12 bg-chsc-indigo-500 rounded-[40px]">
                <header class="mb-6 text-center">
                    <h2 class="mb-8 fs-6xl text-white font-eb-garamond">
                        {{ blok?.title }}
                    </h2>
                    <h3 class="fs-6xl text-chsc-yellow font-londrina-solid">
                        {{ blok?.claim }}
                    </h3>
                </header>
                <div class="flex items-center gap-x-6 max-[641px]:flex-col max-[641px]:gap-x-0 max-[641px]:gap-y-6">
                    <div class="flex-1">
                        <p class="fs-2xl text-white font-londrina-solid">
                            {{ blok?.description_left }}
                        </p>
                    </div>
                    <img
                        v-motion-slide-visible-once-top
                        :src="blok?.description_image.filename"
                        :alt="blok?.description_image.alt"
                        class="max-[769px]:hidden"
                    >
                    <div class="flex-1">
                        <p class="fs-2xl text-white font-londrina-solid">
                            {{ blok?.description_right }}
                        </p>
                    </div>
                </div>

                <UiContainer
                    max-width="max-w-6xl"
                    class="mt-12 max-[641px]:px-0"
                >
                
                    <div class="flex items-end bg-chsc-yellow rounded-[30px] px-6 font-londrina-solid ">
                        <div>
                            <img :src="blok?.quote_image.filename" :alt="blok?.quote_image.alt" class="relative top-px min-w-[240px] max-w-[240px]">
                        </div>
                        <div class="pt-12 py-12">
                            <p class="text-chsc-indigo-500 fs-3xl">{{ blok?.quote_text }}</p>
                            <p class="text-chsc-coral fs-xl">{{ blok?.quote_author }}</p>
                        </div>
                    </div>
                    
                   <!--  <h4 class="font-londrina-solid fs-4xl text-chsc-yellow text-center">
                        {{ blok?.resume }}
                    </h4> -->
                </UiContainer>
                <div class="mt-14 grid grid-cols-2 gap-x-10 gap-y-20 max-[769px]:grid-cols-1">
                    <div class="insight-key">
                        <div class="insight-key__data">
                            <div class="insight-key__card">
                                <StoryblokRichText :doc="blok?.transparency_card" />
                            </div>
                            <ClientOnly>
                                <div class="relative mt-2.5 max-[641px]:inline-flex">
                                    <apex-charts
                                        v-if="blok"
                                        type="donut"
                                        width="100%"
                                        height="300"
                                        :options="
                                            {
                                                chart: {
                                                    type: 'donut',
                                                },
                                                colors: ['#FFD867', '#9780BC'],
                                                stroke: {
                                                    show: false,
                                                },
                                                legend: {
                                                    show: false,
                                                },
                                                dataLabels: {
                                                    enabled: false,
                                                },
                                                tooltip: {
                                                    enabled: false,
                                                },
                                                responsive: [{
                                                    breakpoint: 480,
                                                    options: {
                                                        chart: {
                                                            width: 200,
                                                        },
                                                        legend: {
                                                            position: 'bottom',
                                                        },
                                                    },
                                                }],
                                            }"
                                        :series="[Number(blok?.transparency_chart_data), 100 - Number(blok?.transparency_chart_data)]"
                                    />
                                    <p class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 fs-3xl text-white font-londrina-solid z-1">
                                        {{ blok?.transparency_chart_data }}%
                                    </p>
                                </div>
                            </ClientOnly>
                            <p class="mt-1.5 fs-lg text-chsc-yellow font-londrina-solid uppercase text-center leading-[20px]">
                                {{ blok?.transparency_chart_legend }}
                            </p>
                        </div>
                        <div class="insight-key__description">
                            <StoryblokRichText :doc="blok?.transparency_description" />
                        </div>
                    </div>
                    <div class="insight-key">
                        <div class="insight-key__data">
                            <div class="insight-key__card">
                                <StoryblokRichText :doc="blok?.retailers_card" />
                            </div>
                            <div class="mt-6">
                                <img
                                    :src="blok?.retailers_image.filename"
                                    :alt="blok?.retailers_image.alt"
                                >
                            </div>
                        </div>
                        <div class="insight-key__description">
                            <StoryblokRichText :doc="blok?.retailers_description" />
                        </div>
                    </div>
                    <div class="insight-key">
                        <div class="insight-key__data">
                            <div class="insight-key__card">
                                <StoryblokRichText :doc="blok?.toxic_card" />
                            </div>
                            <div class="-mt-2 -ml-6">
                                <img
                                    v-motion-slide-visible-once-top
                                    :src="blok?.toxic_image.filename"
                                    :alt="blok?.toxic_image.alt"
                                    class="max-w-none"
                                >
                            </div>
                        </div>
                        <div class="insight-key__description">
                            <StoryblokRichText :doc="blok?.toxic_description" />
                        </div>
                    </div>
                    <div class="insight-key">
                        <div class="insight-key__data">
                            <div class="insight-key__card">
                                <StoryblokRichText :doc="blok?.fair_pay_card" />
                            </div>
                            <div class="mt-6">
                                <img
                                    v-motion-slide-visible-once-top
                                    :src="blok?.fair_pay_image.filename"
                                    :alt="blok?.fair_pay_image.alt"
                                    class="max-w-none"
                                >
                            </div>
                        </div>
                        <div class="insight-key__description">
                            <StoryblokRichText :doc="blok?.fair_pay_description" />
                        </div>
                    </div>
                    <div class="insight-key">
                        <div class="insight-key__data">
                            <div class="insight-key__card">
                                <StoryblokRichText :doc="blok?.deforestation_card" />
                            </div>
                            <div class="mt-6 flex flex-col justify-center ">
                                <p class="fs-5xl text-white font-londrina-solid text-center z-1">
                                    {{ blok?.deforestation_percentage }}%
                                </p>
                                <img
                                    v-motion-slide-visible-once-top
                                    :src="blok?.deforestation_image.filename"
                                    :alt="blok?.deforestation_image.alt"
                                    class="mx-auto -mt-4"
                                >
                            </div>
                            <p class="mt-1.5 fs-lg text-chsc-yellow font-londrina-solid uppercase text-center leading-[20px]">
                                {{ blok?.deforestation_legend }}
                            </p>
                        </div>
                        <div class="insight-key__description">
                            <StoryblokRichText :doc="blok?.deforestation_description" />
                        </div>
                    </div>
                    <div class="insight-key">
                        <div class="insight-key__data">
                            <div class="insight-key__card">
                                <StoryblokRichText :doc="blok?.child_labor_card" />
                            </div>
                            <div class="mt-6">
                                <img
                                    v-motion-slide-visible-once-top
                                    :src="blok?.child_labor_image.filename"
                                    :alt="blok?.child_labor_image.alt"
                                    class="mx-auto"
                                >
                            </div>
                        </div>
                        <div class="insight-key__description">
                            <StoryblokRichText :doc="blok?.child_labor_description" />
                        </div>
                    </div>
                </div>
                <div class="mt-20 grid grid-cols-2 gap-x-10 max-[769px]:grid-cols-1 max-[769px]:gap-x-0 max-[769px]:gap-y-10">
                    <div class="[&>h3]:fs-6xl [&>h3]:font-londrina-solid [&>h3]:text-chsc-yellow [&>h3]:mb-8 [&>p]:fs-xl [&>p]:font-londrina-solid [&>p]:text-white">
                        <StoryblokRichText :doc="blok?.resume_description" />
                    </div>
                    <div class="grid grid-cols-2 items-end max-[641px]:grid-cols-1 max-[641px]:gap-y-6">
                        <div class="grid grid-cols-2 gap-x-16">
                            <div class="flex flex-col gap-y-2">
                                <div class="flex-1 flex items-end mb-2.5">
                                    <p class="font-londrina-solid fs-sm text-chsc-yellow text-center">
                                        {{ blok?.resume_bar1_legend }}
                                    </p>
                                </div>
                                <div class="w-full h-[202px] bg-chsc-yellow font-londrina-solid text-chsc-indigo-500 uppercase text-center px-2.5 py-6">
                                    <p class="fs-4xl leading-[28px]">
                                        ${{ blok?.resume_bar1_number }}
                                    </p>
                                    <p class="fs-sm">
                                        {{ blok?.resume_bar_text }}
                                    </p>
                                </div>
                            </div>
                            <div class="flex flex-col gap-y-2">
                                <div class="flex-1 flex items-end mb-2.5">
                                    <p class="font-londrina-solid fs-sm text-chsc-yellow text-center">
                                        {{ blok?.resume_bar2_legend }}
                                    </p>
                                </div>
                                <div class="w-full h-[280px] bg-chsc-yellow font-londrina-solid text-chsc-indigo-500 uppercase text-center px-2.5 py-6">
                                    <p class="fs-4xl leading-[28px]">
                                        ${{ blok?.resume_bar2_number }}
                                    </p>
                                    <p class="fs-sm">
                                        {{ blok?.resume_bar_text }}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="pl-4 relative">
                            <p class="font-londrina-solid fs-2xl text-chsc-yellow text-right uppercase whitespace-pre-line">
                                {{ blok?.resume_bars_claim }}
                            </p>
                            <img
                                v-motion-slide-visible-once-left
                                :src="blok?.resume_image.filename"
                                :alt="blok?.resume_image.alt"
                            >
                        </div>
                    </div>
                </div>
            </div>
        </UiContainer>
    </section>
</template>
